import { FormProvider, useForm } from "react-hook-form";
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useIntl } from "react-intl";

import { validateFedexAddress, validateFedexInvoice, validateFedexPin } from "api";
import Button from "components/v2/Button/Button";
import errorToast from "helpers/errorToast";
import useUnsavedChangesWarning from "hooks/useUnsavedChangesWarning";
import Alert from "components/v2/Alert/Alert";
import { useCreateParcelAccountContext } from "../CreateParcelAccountState";
import CreateParcelAccountVerificationItem, {
  CreateParcelAccountVerificationItemProps,
} from "./CreateParcelAccountVerificationItem";
import CreateParcelAccountInvoiceVerification from "./CreateParcelAccountInvoiceVerification";

interface AccountDetailsPayload {
  is_active: boolean;
  account_number: string;
  account_name: string;
  account_type: "company" | "general";
  available_to_locations: string[];
  fedex_end_user_license_agreement: boolean;
}

interface ValidateFedexPinPayload {
  pin: string;
  account_details: AccountDetailsPayload;
}

interface ValidateFedexInvoicePayload {
  invoice_number: string;
  invoice_date: Date | null;
  invoice_amount: string;
  invoice_currency: string;
  account_details: AccountDetailsPayload;
}

const PRIORITY_MAP: Record<string, number> = {
  SMS: 0,
  CALL: 1,
  EMAIL: 2,
};

function CreateParcelAccountVerificationForm() {
  const { state } = useCreateParcelAccountContext();
  const { addressValidationResponse, addressFormData } = state;
  const { account_details: accountDetails } = addressFormData || {};
  const navigate = useNavigate();
  const intl = useIntl();
  const hasRetriedSessionRef = useRef({
    pin: false,
    invoice: false,
  });

  const { UnsavedChangesModal, confirmNavigation } = useUnsavedChangesWarning({
    isDirty: true,
    title: intl.formatMessage({
      id: "settings.parcelAccounts.createAccount.unsavedTitle",
      defaultMessage: "Discard account creation?",
    }),
    message: intl.formatMessage({
      id: "settings.parcelAccounts.createAccount.unsavedMessage",
      defaultMessage: "All of your entered information will be lost. Proceed?",
    }),
  });

  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      mfa_option: "SMS",
      pin: "",
      invoice_number: "",
      invoice_amount: "",
      invoice_currency: "",
      invoice_date: null,
    },
  });

  const { watch, handleSubmit } = methods;
  const [mfaOption, pin, invoiceNumber, invoiceAmount, invoiceCurrency, invoiceDate] = watch([
    "mfa_option",
    "pin",
    "invoice_number",
    "invoice_amount",
    "invoice_currency",
    "invoice_date",
  ]);
  const [maxRetriesReached, setMaxRetriesReached] = useState(false);
  const [failedMethods, setFailedMethods] = useState({
    SMS: false,
    EMAIL: false,
    CALL: false,
    INVOICE: false,
  });

  const sortedMfaOptions = useMemo(() => {
    if (!addressValidationResponse?.mfa_options?.secureCode?.length) {
      return [];
    }
    return addressValidationResponse.mfa_options.secureCode
      .slice()
      .sort((a: string, b: string) => PRIORITY_MAP[a] - PRIORITY_MAP[b]);
  }, [addressValidationResponse]);

  const isDisabled =
    (["SMS", "EMAIL", "CALL"].includes(mfaOption) && !pin) ||
    (mfaOption === "INVOICE" && (!invoiceNumber || !invoiceAmount || !invoiceCurrency || !invoiceDate));

  const MFA_OPTION_CONFIG: Record<
    string,
    Omit<CreateParcelAccountVerificationItemProps, "maxRetriesReached" | "setMaxRetriesReached">
  > = {
    SMS: {
      radioOptions: [
        {
          label: intl.formatMessage({
            id: "settings.parcelAccounts.createAccount.mfa.smsLabel",
            defaultMessage: "SMS",
          }),
          value: "SMS",
        },
      ],
      description: intl.formatMessage({
        id: "settings.parcelAccounts.createAccount.mfa.smsDescription",
        defaultMessage: "Receive a code on the phone number linked to your FedEx account",
      }),
      label: intl.formatMessage({
        id: "settings.parcelAccounts.createAccount.mfa.phoneLabel",
        defaultMessage: "Phone number",
      }),
      placeholder: addressValidationResponse?.phone_number || "",
    },
    EMAIL: {
      radioOptions: [
        {
          label: intl.formatMessage({
            id: "settings.parcelAccounts.createAccount.mfa.emailLabel",
            defaultMessage: "Email",
          }),
          value: "EMAIL",
        },
      ],
      description: intl.formatMessage({
        id: "settings.parcelAccounts.createAccount.mfa.emailDescription",
        defaultMessage: "Receive a code at the email address linked to your FedEx account",
      }),
      label: intl.formatMessage({
        id: "settings.parcelAccounts.createAccount.mfa.emailLabel",
        defaultMessage: "Email",
      }),
      placeholder: addressValidationResponse?.email || "",
    },
    CALL: {
      radioOptions: [
        {
          label: intl.formatMessage({
            id: "settings.parcelAccounts.createAccount.mfa.callLabel",
            defaultMessage: "Call",
          }),
          value: "CALL",
        },
      ],
      description: intl.formatMessage({
        id: "settings.parcelAccounts.createAccount.mfa.callDescription",
        defaultMessage: "Receive a code via a voice call to the phone number linked to your FedEx account",
      }),
      label: intl.formatMessage({
        id: "settings.parcelAccounts.createAccount.mfa.phoneLabel",
        defaultMessage: "Phone number",
      }),
      placeholder: addressValidationResponse?.phone_number || "",
    },
  };

  const handleSuccess = () => {
    toast.success(
      intl.formatMessage({
        id: "settings.parcelAccounts.createAccount.verifySuccess",
        defaultMessage: "FedEx account verified successfully",
      }),
    );
    navigate("/settings/parcel-accounts", { replace: true });
  };

  const handleError = (error: any) => {
    error.response.data = error.response.data?.non_field_errors || error.response.data;
    errorToast(error);
  };

  const recordFailure = (method: "SMS" | "EMAIL" | "CALL" | "INVOICE") => {
    setFailedMethods((prev) => ({
      ...prev,
      [method]: true,
    }));
  };

  const { mutateAsync: validatePin, isLoading: isValidatingPin } = useMutation({
    mutationFn: (payload: ValidateFedexPinPayload) => validateFedexPin(payload),
    onSuccess: handleSuccess,
  });

  const { mutateAsync: validateInvoice, isLoading: isValidatingInvoice } = useMutation({
    mutationFn: (payload: ValidateFedexInvoicePayload) => validateFedexInvoice(payload),
    onSuccess: handleSuccess,
  });

  const buildAddressValidationPayload = () => ({
    ...addressFormData,
    account_details: {
      ...addressFormData?.account_details,
      available_to_locations: addressFormData?.account_details?.available_to_locations || [],
    },
  });

  const isSessionExpiredError = (error: any) =>
    [
      "FedEx Error: Please validate address first.",
      "FedEx Error: Session has expired. Please validate address again.",
    ].includes(error?.response?.data?.non_field_errors?.[0]);

  const runWithSessionRetry = async (
    key: "pin" | "invoice",
    action: () => Promise<unknown>,
    onFinalError: (error: any) => void,
  ) => {
    try {
      await action();
      hasRetriedSessionRef.current[key] = false;
      return;
    } catch (error: any) {
      if (isSessionExpiredError(error) && !hasRetriedSessionRef.current[key] && addressFormData) {
        hasRetriedSessionRef.current[key] = true;
        try {
          await validateFedexAddress(buildAddressValidationPayload());
          await action();
          return;
        } catch (retryError: any) {
          onFinalError(retryError);
          return;
        }
      }
      onFinalError(error);
    }
  };

  const onSubmit = async (data: any) => {
    if (!accountDetails) {
      toast.error(
        intl.formatMessage({
          id: "settings.parcelAccounts.createAccount.detailsMissingError",
          defaultMessage: "Account details are missing. Please re-enter your address information.",
        }),
      );
      return;
    }

    const accountDetailsWithLocations = {
      ...accountDetails,
      available_to_locations: accountDetails.available_to_locations || [],
    };

    if (["SMS", "EMAIL", "CALL"].includes(data.mfa_option)) {
      const payload: ValidateFedexPinPayload = {
        pin: data.pin,
        account_details: accountDetailsWithLocations,
      };
      await runWithSessionRetry(
        "pin",
        () => validatePin(payload),
        (error) => {
          recordFailure(data.mfa_option);
          handleError(error);
        },
      );
      return;
    }

    const payload: ValidateFedexInvoicePayload = {
      invoice_number: data.invoice_number,
      invoice_date: data.invoice_date,
      invoice_amount: data.invoice_amount,
      invoice_currency: data.invoice_currency,
      account_details: accountDetailsWithLocations,
    };
    await runWithSessionRetry(
      "invoice",
      () => validateInvoice(payload),
      (error) => {
        recordFailure("INVOICE");
        handleError(error);
      },
    );
  };

  const handleDiscard = () => {
    confirmNavigation(() => {
      navigate("/settings/parcel-accounts", { replace: true });
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="text-lg font-semibold text-primary">
          {intl.formatMessage({
            id: "settings.parcelAccounts.createAccount.verifyTitle",
            defaultMessage: "Verify your FedEx account",
          })}
        </div>
        <div className="text-sm text-tertiary">
          {intl.formatMessage({
            id: "settings.parcelAccounts.createAccount.verifySubtitle",
            defaultMessage: "You can use the information associated with the account number being registered",
          })}
        </div>
        {failedMethods.SMS && failedMethods.EMAIL && failedMethods.CALL && failedMethods.INVOICE && (
          <div className="mt-xl">
            <Alert
              label={intl.formatMessage({
                id: "settings.parcelAccounts.createAccount.verifyFailedLabel",
                defaultMessage: "Verification failed",
              })}
              variant="error"
            >
              {intl.formatMessage({
                id: "settings.parcelAccounts.createAccount.verifyFailedMessage",
                defaultMessage:
                  "We are unable to process this request. Please try again later or call FedEx Customer Service and ask for technical support",
              })}
            </Alert>
          </div>
        )}
        {maxRetriesReached && (
          <div className="mt-xl">
            <Alert
              label={intl.formatMessage({
                id: "settings.parcelAccounts.createAccount.maxCodeRequestsLabel",
                defaultMessage: "Code request maxed out",
              })}
              variant="error"
            >
              {intl.formatMessage({
                id: "settings.parcelAccounts.createAccount.maxCodeRequestsMessage",
                defaultMessage:
                  "The maximum amount of code has been reached. We are unable to process this request. Please try again later or call FedEx Customer Service and ask for technical support",
              })}
            </Alert>
          </div>
        )}
        {sortedMfaOptions.map((option) => (
          <CreateParcelAccountVerificationItem
            key={option}
            {...MFA_OPTION_CONFIG[option]}
            maxRetriesReached={maxRetriesReached}
            setMaxRetriesReached={setMaxRetriesReached}
          />
        ))}
        {addressValidationResponse?.mfa_options?.invoice && <CreateParcelAccountInvoiceVerification />}
        <div className="mt-xl d-flex align-items-center justify-content-end">
          <Button
            variant="secondary-gray"
            label={intl.formatMessage({
              id: "settings.parcelAccounts.createAccount.discard",
              defaultMessage: "Discard",
            })}
            size="md"
            type="button"
            className="mr-md"
            onClick={handleDiscard}
          />
          <Button
            variant="primary"
            label={
              isValidatingPin || isValidatingInvoice
                ? intl.formatMessage({
                    id: "settings.parcelAccounts.createAccount.verifyVerifying",
                    defaultMessage: "Verifying...",
                  })
                : intl.formatMessage({
                    id: "settings.parcelAccounts.createAccount.verifyButton",
                    defaultMessage: "Verify",
                  })
            }
            size="md"
            type="submit"
            disabled={isDisabled || isValidatingPin || isValidatingInvoice}
          />
        </div>
        <UnsavedChangesModal />
      </form>
    </FormProvider>
  );
}

export default CreateParcelAccountVerificationForm;
